import React from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Truck } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { PageMeta } from "@/components/PageMeta";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CtaBand } from "@/components/CtaBand";
import { SERVICES, EXTRA_SERVICES, IMAGES, BRAND, waLink } from "@/config/brand";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { QuoteForm } from "@/components/quote/QuoteForm";

const copy = {
    "house-removals": {
        intro: "Whether you’re moving a family home or a shared house, we plan around your inventory, access and dates so the day runs smoothly.",
        includes: ["Careful loading and unloading", "Protective materials for furniture", "Optional dismantling and reassembly", "Optional packing help and materials"],
        suitable: "Perfect for family homes, shared houses and larger flats where a full team and appropriate vehicle are needed.",
    },
    "flat-removals": {
        intro: "Flat moves in London often involve stairs, narrow access and permit-only parking. We come prepared and plan the route ahead of time.",
        includes: ["Stair and lift access planning", "Trolleys and protective blankets", "Careful handling of shared entrances", "Advice on parking and permits"],
        suitable: "Studios and one- to three-bedroom flats across London.",
    },
    "office-removals": {
        intro: "We help small and medium London offices relocate with minimal downtime, working around your schedule where possible.",
        includes: ["Furniture and IT equipment handling", "Weekend and out-of-hours moves", "Labelled boxes and inventory", "Reassembly at your new office"],
        suitable: "Startups, agencies and small teams relocating within London.",
    },
    "man-and-van": {
        intro: "Flexible man-and-van support for smaller jobs, part-loads, deliveries and short-notice moves.",
        includes: ["One friendly driver and helper", "Right-sized van for the job", "Loading and unloading assistance", "Great for shorter jobs"],
        suitable: "Part-move jobs, marketplace pickups, single-room clearances.",
    },
    "furniture-delivery": {
        intro: "Pickup and delivery for new or used furniture across London — with care from door to door.",
        includes: ["Protective wrapping for the journey", "Two-person lifts where required", "Optional assembly on delivery", "Old-item removal on request"],
        suitable: "Marketplace pickups, showroom collections and gifts.",
    },
    "packing-assistance": {
        intro: "Save time on moving day — we can supply the materials and pack for you, or work alongside you.",
        includes: ["Boxes, tape and protective wrap", "Full or partial packing service", "Fragile items packed with care", "Labelled by room for fast unpacking"],
        suitable: "Anyone short on time or moving delicate items.",
    },
    "single-item-delivery": {
        intro: "One item, wardrobe, sofa or appliance? Book a fast, careful single-item move.",
        includes: ["Two-person lift where needed", "Wrapping and protection", "Weekend availability", "Simple, clear pricing"],
        suitable: "Single items across London — large or small.",
    },
    "student-moves": {
        intro: "Affordable moves between student halls, flats and storage — planned around your term dates.",
        includes: ["Small or large loads", "Storage-friendly boxes", "Weekend availability", "Friendly, careful team"],
        suitable: "Students moving in, out or between accommodations.",
    },
    "same-day-removals": {
        intro: "Short-notice removals subject to availability. Message us on WhatsApp for the quickest response.",
        includes: ["Same-day slot subject to availability", "Fast quote turnaround", "Small or medium loads", "Weekend availability"],
        suitable: "Emergency moves, unexpected changes of plan.",
    },
};

export default function ServiceDetail() {
    const { slug } = useParams();
    const service = [...SERVICES, ...EXTRA_SERVICES].find((s) => s.slug === slug);
    if (!service) {
        return (
            <div className="container-ys section">
                <h1 className="text-2xl font-semibold">Service not found</h1>
                <Link to="/services" className="text-[#001B3D]">Back to services</Link>
            </div>
        );
    }
    const Icon = LucideIcons[service.icon] || Truck;
    const c = copy[slug] || {};
    const related = [...SERVICES, ...EXTRA_SERVICES].filter((s) => s.slug !== slug).slice(0, 3);

    const faqs = [
        { q: `How is a ${service.title.toLowerCase()} quote calculated?`, a: `We consider your inventory, access, distance, dates and any packing help required. We’ll send a clear quotation tailored to your job.` },
        { q: "Do you cover all of London?", a: "Yes — we work across Greater London and many surrounding areas." },
        { q: "Can I book at short notice?", a: "Short-notice bookings are subject to availability. WhatsApp is the fastest way to check." },
        { q: "Do you provide packing materials?", a: "Yes, materials and hands-on packing help are available as an add-on." },
    ];

    return (
        <div>
            <PageMeta
                title={`${service.title} in London — YS Removals`}
                description={`${service.title} across London. ${service.short}`}
                canonical={`https://${BRAND.domain}/services/${slug}`}
                ldJson={{ "@context": "https://schema.org", "@type": "Service", name: service.title, provider: { "@type": "MovingCompany", name: BRAND.name }, areaServed: "London" }}
            />
            <section className="bg-[#001B3D] text-white">
                <div className="container-ys section">
                    <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: service.title }]} />
                    <div className="mt-3 flex items-start gap-4">
                        <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#001B3D]/15 text-[#001B3D] border border-[#001B3D]/25"><Icon className="h-6 w-6" /></span>
                        <div>
                            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight font-heading">{service.title} in London</h1>
                            <p className="mt-2 text-white/85 max-w-2xl">{c.intro || service.short}</p>
                        </div>
                    </div>
                </div>
            </section>
            <section className="container-ys section grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">
                    <div>
                        <h2 className="text-2xl font-heading font-semibold">What’s included</h2>
                        <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                            {(c.includes || []).map((x) => (
                                <li key={x} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-1 text-[#001B3D]" /> <span>{x}</span></li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <h2 className="text-2xl font-heading font-semibold">Who it’s suitable for</h2>
                        <p className="mt-3 text-[#17252D]/80">{c.suitable}</p>
                    </div>
                    <div>
                        <h2 className="text-2xl font-heading font-semibold">How the process works</h2>
                        <ol className="mt-3 grid gap-3 sm:grid-cols-2">
                            {["Tell us about your job", "Receive a tailored quotation", "Confirm your booking", "We complete your move"].map((s, i) => (
                                <li key={s} className="rounded-xl border border-[#17252D]/10 p-4">
                                    <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#001B3D] text-white text-sm font-semibold">{i + 1}</div>
                                    <div className="mt-2 font-medium">{s}</div>
                                </li>
                            ))}
                        </ol>
                    </div>
                    <div>
                        <h2 className="text-2xl font-heading font-semibold">Preparing for the day</h2>
                        <ul className="mt-3 space-y-2 text-sm text-[#17252D]/80">
                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-1 text-[#001B3D]" /> Confirm parking and access at both properties.</li>
                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-1 text-[#001B3D]" /> Label boxes by room to help with unloading.</li>
                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-1 text-[#001B3D]" /> Keep valuables and documents with you.</li>
                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-1 text-[#001B3D]" /> Let us know about any fragile items in advance.</li>
                        </ul>
                    </div>
                    <div>
                        <h2 className="text-2xl font-heading font-semibold">Related services</h2>
                        <div className="mt-3 grid gap-3 sm:grid-cols-3">
                            {related.map((r) => (
                                <Link key={r.slug} to={`/services/${r.slug}`} className="rounded-xl border border-[#17252D]/10 p-4 hover:border-[#001B3D]/40">
                                    <div className="font-medium">{r.title}</div>
                                    <div className="mt-1 text-xs text-[#17252D]/70">{r.short}</div>
                                </Link>
                            ))}
                        </div>
                    </div>
                    <div>
                        <h2 className="text-2xl font-heading font-semibold">FAQs</h2>
                        <div className="mt-3 rounded-xl border border-[#17252D]/10 p-2">
                            <Accordion type="single" collapsible>
                                {faqs.map((f, i) => (
                                    <AccordionItem key={i} value={`${slug}-${i}`}>
                                        <AccordionTrigger className="font-heading text-left">{f.q}</AccordionTrigger>
                                        <AccordionContent className="text-[#17252D]/80">{f.a}</AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <a href={waLink()} target="_blank" rel="noreferrer"><Button className="bg-[#25D366] hover:bg-[#20b955] text-white font-semibold h-11 px-6">WhatsApp us</Button></a>
                        <Link to="/contact"><Button variant="outline" className="h-11 px-6">Contact us</Button></Link>
                    </div>
                </div>
                <aside id="quote" className="lg:sticky lg:top-24 h-fit">
                    <QuoteForm defaultService={service.title} />
                </aside>
            </section>
            <CtaBand subtitle={`Ready to book your ${service.title.toLowerCase()}? Call, WhatsApp or start your quote.`} />
        </div>
    );
}
